import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["label", "children", "size", "placement", "open", "defaultFocusElement", "contentRef", "shouldContainFocus", "shouldReturnFocus", "shouldCloseOnDocumentClick", "onOpen", "onClose", "onDismiss", "mountNode", "insertAt", "liveRegion", "onEnter", "onEntering", "onEntered", "onExit", "onExiting", "onExited", "onTransition", "transitionOnMount", "transitionEnter", "transitionExit", "border", "shadow", "role"];
var _dec, _dec2, _dec3, _class, _class2;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/** @jsx jsx */
import { Component } from 'react';
import { Dialog } from '@instructure/ui-dialog';
import { omitProps } from '@instructure/ui-react-utils';
import { createChainedFunction } from '@instructure/ui-utils';
import { textDirectionContextConsumer } from '@instructure/ui-i18n';
import { testable } from '@instructure/ui-testable';
import { Portal } from '@instructure/ui-portal';
import { mirrorHorizontalPlacement } from '@instructure/ui-position';
import { Transition } from '@instructure/ui-motion';
import { withStyle, jsx } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
/**
---
category: components
---
@tsProps
**/
let Tray = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = textDirectionContextConsumer(), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_class2 = class Tray extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      transitioning: false
    };
    this.ref = null;
    this.handleTransitionComplete = _type => {
      this.setState({
        transitioning: false
      });
    };
    this.handlePortalOpen = DOMNode => {
      if (DOMNode) {
        this.DOMNode = DOMNode;
      }
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
  }
  componentDidUpdate(prevProps, _prevState) {
    var _this$props$makeStyle2, _this$props2;
    if (this.props.open !== prevProps.open) {
      this.setState({
        transitioning: true
      });
    }
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  get placement() {
    const _this$props3 = this.props,
      placement = _this$props3.placement,
      dir = _this$props3.dir;
    const isRtl = dir === textDirectionContextConsumer.DIRECTION.rtl;
    return isRtl ? mirrorHorizontalPlacement(placement, ' ') : placement;
  }
  get direction() {
    switch (this.placement) {
      case 'top':
        return 'up';
      case 'bottom':
        return 'down';
      case 'end':
        return 'right';
      default:
        // start
        return 'left';
    }
  }
  get transition() {
    return `slide-${this.direction}`;
  }
  get DOMNode() {
    // The DOMNode property is needed for Portal type components
    return this.ref;
  }
  set DOMNode(el) {
    this.ref = el ? el : null;
  }
  render() {
    var _this$props$styles, _this$props$styles2;
    const _this$props4 = this.props,
      label = _this$props4.label,
      children = _this$props4.children,
      size = _this$props4.size,
      placement = _this$props4.placement,
      open = _this$props4.open,
      defaultFocusElement = _this$props4.defaultFocusElement,
      contentRef = _this$props4.contentRef,
      shouldContainFocus = _this$props4.shouldContainFocus,
      shouldReturnFocus = _this$props4.shouldReturnFocus,
      shouldCloseOnDocumentClick = _this$props4.shouldCloseOnDocumentClick,
      onOpen = _this$props4.onOpen,
      onClose = _this$props4.onClose,
      onDismiss = _this$props4.onDismiss,
      mountNode = _this$props4.mountNode,
      insertAt = _this$props4.insertAt,
      liveRegion = _this$props4.liveRegion,
      onEnter = _this$props4.onEnter,
      onEntering = _this$props4.onEntering,
      onEntered = _this$props4.onEntered,
      onExit = _this$props4.onExit,
      onExiting = _this$props4.onExiting,
      onExited = _this$props4.onExited,
      onTransition = _this$props4.onTransition,
      transitionOnMount = _this$props4.transitionOnMount,
      transitionEnter = _this$props4.transitionEnter,
      transitionExit = _this$props4.transitionExit,
      border = _this$props4.border,
      shadow = _this$props4.shadow,
      role = _this$props4.role,
      props = _objectWithoutProperties(_this$props4, _excluded);
    const portalIsOpen = open || this.state.transitioning;
    return jsx(Portal, {
      open: portalIsOpen,
      onOpen: this.handlePortalOpen,
      insertAt: insertAt,
      mountNode: mountNode
    }, portalIsOpen && jsx(Transition, {
      in: open,
      type: this.transition,
      onTransition: onTransition,
      onEnter: onEnter,
      onEntering: onEntering,
      onEntered: createChainedFunction(this.handleTransitionComplete, onEntered, onOpen),
      onExit: onExit,
      onExiting: onExiting,
      onExited: createChainedFunction(this.handleTransitionComplete, onExited, onClose),
      transitionOnMount: transitionOnMount,
      transitionEnter: transitionEnter,
      transitionExit: transitionExit
    }, jsx("span", Object.assign({}, omitProps(props, Tray.allowedProps), {
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.tray,
      ref: contentRef
    }), this.state.transitioning ? children : jsx(Dialog, {
      as: "div",
      label: label,
      defaultFocusElement: defaultFocusElement,
      open: true,
      shouldContainFocus: shouldContainFocus,
      shouldReturnFocus: shouldReturnFocus,
      shouldCloseOnDocumentClick: shouldCloseOnDocumentClick,
      shouldCloseOnEscape: true,
      liveRegion: liveRegion,
      onDismiss: onDismiss,
      role: role
    }, jsx("div", {
      css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.content
    }, children)))));
  }
}, _class2.displayName = "Tray", _class2.componentId = 'Tray', _class2.allowedProps = allowedProps, _class2.propTypes = propTypes, _class2.defaultProps = {
  defaultFocusElement: null,
  open: false,
  insertAt: 'bottom',
  shouldCloseOnDocumentClick: false,
  shouldContainFocus: true,
  shouldReturnFocus: true,
  size: 'small',
  placement: 'start',
  transitionOnMount: true,
  transitionEnter: true,
  transitionExit: true,
  shadow: true,
  border: false
}, _class2)) || _class) || _class) || _class);
export default Tray;
export { Tray };